import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { Loader } from 'rsuite';
import { Table, TableBody, TableCell, TableRow } from '@mui/material';
import { fetchBalanceHistory } from '@app/store/slices/referralsSlice';
import EmptyData from '@shared/ui/messages/EmptyData';
import { formatBalanceUi } from '@utils/balance-helpers';
import { formatMonth, formatTime } from '@utils/date-helpers';
import { referralsBalanceHistoryColumns } from '@utils/tables/columns';
import CustomTableHead from '@widgets/tables/main/CustomTableHead';

const COUNT = 20;

export default function ReferralsBalanceHistory() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [start, setStart] = useState(1);
  const { balanceHistory, balanceHistoryCount, isBalanceHistoryLoading } =
    useSelector((state) => state.referrals);

  useEffect(() => {
    dispatch(fetchBalanceHistory({ count: COUNT, start: 1 }));
  }, [dispatch]);

  const fetchNext = useCallback(() => {
    const nextStart = start + COUNT;
    setStart(nextStart);
    dispatch(fetchBalanceHistory({ count: COUNT, start: nextStart }));
  }, [start, dispatch]);

  return (
    <div
      id="referrals-history-scroll-container"
      className="block-pd-x drawerFilter-grow"
    >
      {balanceHistory.length === 0 ? (
        isBalanceHistoryLoading ? (
          <Loader center />
        ) : (
          <EmptyData text={t('nothing_yet')} />
        )
      ) : (
        <InfiniteScroll
          dataLength={balanceHistory.length}
          next={fetchNext}
          hasMore={balanceHistory.length < balanceHistoryCount}
          loader={
            <div className="referrals-history__loader">
              <Loader />
            </div>
          }
          scrollableTarget="referrals-history-scroll-container"
          scrollThreshold={0.9}
          style={{ overflow: 'visible' }}
        >
          <Table className="custom-table referrals-history" stickyHeader>
            <CustomTableHead columns={referralsBalanceHistoryColumns} />
            <TableBody>
              {balanceHistory.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <p>{formatMonth(row.created_at)}</p>
                    <p className="secondary-text">
                      {formatTime(row.created_at)}
                    </p>
                  </TableCell>
                  <TableCell>
                    {row.type === 'income'
                      ? t('referral_income_from', {
                          name: row.referral_user_name,
                        })
                      : t('Withdrawal to balance')}
                  </TableCell>
                  <TableCell align="right">
                    <p
                      className={
                        row.balance_change > 0 ? 'positive-num' : 'negative-num'
                      }
                    >
                      {row.balance_change > 0 ? '+' : '-'}
                      {formatBalanceUi(Math.abs(row.balance_change), 'USD')}
                    </p>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </InfiniteScroll>
      )}
    </div>
  );
}
